"use client";

import Image from "next/image";
import { useState } from "react";

import { resolveTokenLogo } from "@/data/tokenLogos";

// Deterministic (no network request) colored-circle avatar — ported from
// dotone-swap-v3's src/components/TokenAvatar.tsx. Used as the fallback for
// tokens with no real logo (local/test tokens), and for a bridged token's
// real logo (data/tokenLogos.ts) if that image fails to load.
const PALETTE: [string, string][] = [
  ["#F5BC27", "#c99518"],
  ["#00ff64", "#0a8f3c"],
  ["#ff6400", "#b34700"],
  ["#5b8def", "#2f5bb8"],
  ["#e05bef", "#a52fb8"],
  ["#ff6b6b", "#b33e3e"],
];

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h;
}

export function TokenAvatar({
  symbol,
  address,
  logoUrl: externalLogoUrl,
  size = 36,
  className = "",
}: {
  symbol: string;
  address?: string;
  /** Logo URL supplied directly by the token's own source (TokenInfo.logoUrl,
   * e.g. LI.FI's per-token logoURI) — an arbitrary, unknown-ahead-of-time
   * domain, so rendered via a plain <img> rather than next/image (which
   * requires an allowlisted domain in next.config.ts). Takes priority over
   * data/tokenLogos.ts's symbol-keyed map when present. */
  logoUrl?: string;
  size?: number;
  className?: string;
}) {
  const staticLogoUrl = resolveTokenLogo(symbol);
  const [externalFailed, setExternalFailed] = useState(false);
  const [staticFailed, setStaticFailed] = useState(false);

  if (externalLogoUrl && !externalFailed) {
    return (
      // Arbitrary third-party domain we don't control (LI.FI-supplied
      // per-token logo) — can't use next/image without allowlisting every
      // such domain in next.config.ts ahead of time.
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={externalLogoUrl}
        alt={symbol}
        width={size}
        height={size}
        className={`shrink-0 rounded-full ${className}`}
        onError={() => setExternalFailed(true)}
      />
    );
  }

  if (staticLogoUrl && !staticFailed) {
    return (
      <Image
        src={staticLogoUrl}
        alt={symbol}
        width={size}
        height={size}
        className={`shrink-0 rounded-full ${className}`}
        onError={() => setStaticFailed(true)}
      />
    );
  }

  const key = address || symbol;
  const [from, to] = PALETTE[hashString(key) % PALETTE.length];
  const letters = (symbol.startsWith("W") ? symbol.slice(1) : symbol).slice(0, 2).toUpperCase();

  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full font-semibold text-surface-1 ${className}`}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.36,
        background: `linear-gradient(135deg, ${from}, ${to})`,
      }}
    >
      {letters}
    </div>
  );
}
